import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import useI18n from '@/hooks/useI18n';
import { formatReturn } from '@/utils/numberUtils';
import {
  ROAS_ALL_COLOR,
  ROAS_INSTAGRAM_COLOR,
  ROAS_TIKTOK_COLOR,
} from '@/components/common/campaign/analyticsTokens';

interface ROASChartProps {
  // Nullable so the backend can signal future-pad buckets with `null`
  // (no plot point) vs `0` (real "no ROAS this period").
  allRoas: (number | null)[];
  instagramRoas: (number | null)[];
  tiktokRoas: (number | null)[];
  categories: string[];
  rotateLabels?: boolean;
  /** Index of the category to render in highlight color (#2B6BDB). */
  highlightCategoryIndex?: number;
}

const ROASChart: React.FC<ROASChartProps> = ({
  allRoas,
  instagramRoas,
  tiktokRoas,
  categories,
  rotateLabels = false,
  highlightCategoryIndex,
}) => {
  const { t } = useI18n();
  const shouldRotate = rotateLabels || categories.length > 7;
  const allValues = [...allRoas, ...instagramRoas, ...tiktokRoas].filter(
    (v): v is number => typeof v === 'number' && Number.isFinite(v),
  );
  const maxVal = allValues.length ? Math.max(...allValues) : 0;
  const yMax = maxVal === 0 ? 10 : Math.ceil(maxVal / 2) * 2 + 2;

  // Show "No Data" if every value is null/undefined or 0.
  const hasData = [...allRoas, ...instagramRoas, ...tiktokRoas].some(
    (v) => v !== null && v !== undefined && v !== 0,
  );

  const series = hasData
    ? [
        { name: 'All ROAS', type: 'area' as const, data: allRoas },
        { name: 'Instagram', type: 'line' as const, data: instagramRoas },
        { name: 'TikTok', type: 'line' as const, data: tiktokRoas },
      ]
    : [];

  const chartOptions: ApexOptions = {
    chart: {
      type: 'line',
      height: 350,
      offsetX: -20,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: 'straight',
      width: [2, 2, 2],
      dashArray: [0, 5, 5],
      colors: [ROAS_ALL_COLOR, ROAS_INSTAGRAM_COLOR, ROAS_TIKTOK_COLOR],
    },
    fill: {
      type: ['gradient'],
      colors: ['#ddf4e5'],
      gradient: {
        shade: 'light',
        shadeIntensity: 0,
        opacityFrom: 1,
        opacityTo: 0.1,
        stops: [0, 100],
      },
      opacity: [1, 1, 1],
    },
    colors: [ROAS_ALL_COLOR, ROAS_INSTAGRAM_COLOR, ROAS_TIKTOK_COLOR],
    xaxis: {
      categories,
      tickAmount: categories.length > 1 ? categories.length - 1 : undefined,
      labels: {
        style: {
          colors:
            highlightCategoryIndex !== undefined
              ? categories.map((_, i) =>
                  i === highlightCategoryIndex ? '#2B6BDB' : '#383838',
                )
              : '#383838',
          fontFamily: 'Manrope, sans-serif',
          fontSize: '13px',
          fontWeight: 500,
          cssClass: 'lf-axis-label',
        },
        show: true,
        rotate: shouldRotate ? -45 : 0,
        rotateAlways: shouldRotate,
        trim: false,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: {
        show: true,
        stroke: {
          color: '#191B1C',
          width: 1,
          dashArray: 4,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      min: 0,
      max: yMax,
      tickAmount: 5,
      forceNiceScale: true,
      labels: {
        formatter: (value: number) => formatReturn(value),
        style: {
          colors: '#9B919D',
          fontFamily: 'Manrope, sans-serif',
          fontSize: '13px',
          fontWeight: 500,
          cssClass: 'lf-axis-label',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    grid: {
      show: true,
      padding: {
        left: 10,
        right: 10,
        top: 0,
        bottom: 16,
      },
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
      borderColor: '#E5E9EB',
      strokeDashArray: 4,
    },
    legend: { show: false },
    tooltip: {
      shared: true,
      intersect: false,
      x: { show: false },
      style: { fontSize: '12px' },
      marker: { show: false },
      custom: function ({
        series: s,
        dataPointIndex,
      }: {
        series: (number | null)[][];
        dataPointIndex: number;
      }) {
        const rowStyle =
          'color: #292828; font-family: Manrope, sans-serif; font-size: 14px; line-height: 24px; text-align: left;';
        const cardStyle =
          'background: #fff; border-radius: 12px; padding: 12px 16px; box-shadow: 0 4px 16px rgba(0,0,0,0.08); font-family: Manrope, sans-serif; min-width: 140px;';

        // `null` = future bucket → render `—` instead of `0x` so the user
        // doesn't read a missing data point as "0 ROAS".
        const fmt = (v: number | null | undefined) =>
          v === null || v === undefined
            ? '<strong style="font-weight: 700;">—</strong>'
            : `<strong style="font-weight: 700;">${formatReturn(v)}</strong>`;
        const all = s[0]?.[dataPointIndex];
        const ig = s[1]?.[dataPointIndex];
        const tt = s[2]?.[dataPointIndex];

        return `<div style="${cardStyle}">
          <div style="${rowStyle}">Avg ${fmt(all)}</div>
          <div style="${rowStyle}">Instagram ${fmt(ig)}</div>
          <div style="${rowStyle}">TikTok ${fmt(tt)}</div>
        </div>`;
      },
    },
    noData: {
      text: String(t('common.noData')),
      align: 'center',
      verticalAlign: 'middle',
      offsetX: 20,
      style: {
        color: '#9B919D',
        fontSize: '20px',
        fontFamily: 'Manrope, sans-serif',
      },
    },
  };

  return (
    <div className="[&_.apexcharts-tooltip]:![transform:translateX(-20px)]">
      <Chart options={chartOptions} series={series} type="line" height={350} />
    </div>
  );
};

export default ROASChart;
